import { SafeError, SafePromise } from '@benzinga/safe-await';
import { ListenableSubscribable } from '@benzinga/subscribable';
import { ScannerRequestOptions, ScannerRestful } from './restful';
import { Session } from '@benzinga/session';
import { ScannerProtos, CommonProtos } from './scanner_protos';
import { ScannerConfig } from '@benzinga/scanner-config-manager';
import { configToQuery } from './utils';

interface ErrorEvent {
  error?: SafeError | CommonProtos.IErr | Response;
  errorType: 'get_scanner_instruments_error';
  type: 'error';
}

interface ScannerInstrumentsEvent {
  type: 'get_scanner_instruments';
  scannerResponse: ScannerProtos.IQueryResponse;
}

export type ScannerInstrumentsRequestEvent = ErrorEvent | ScannerInstrumentsEvent;

export interface ScannerRequestErrorData {
  scannerError?: CommonProtos.IErr;
  /** HTTP status of the failed response; absent for network errors, aborts and query-build failures. */
  status?: number;
}
export class ScannerRequest extends ListenableSubscribable<ScannerInstrumentsRequestEvent> {
  private restful: ScannerRestful;
  private session: Session;

  constructor(session: Session) {
    super();
    this.session = session;
    this.restful = new ScannerRestful(session);
  }

  public getScannerInstruments = async (
    config: ScannerConfig,
    options?: ScannerRequestOptions,
  ): SafePromise<ScannerProtos.IScannerResult> => {
    const query = configToQuery(config, this.session);
    if (query.err) {
      return query;
    }
    const response = await this.restful.getScannerInstruments(query.ok, config.source, options);

    if (response.err) {
      return this.failScannerRequest(response.err);
    }

    return this.decodeScannerResult(response.ok);
  };

  public getScannerInstrumentsWithQuery = async (
    query: ScannerProtos.IQuery,
    source: 'stocks' | 'crypto' = 'stocks',
  ): SafePromise<ScannerProtos.IScannerResult> => {
    const response = await this.restful.getScannerInstruments(new ScannerProtos.Query(query), source);

    if (response.err) {
      return this.failScannerRequest(response.err);
    }

    return this.decodeScannerResult(response.ok);
  };

  public getSectorHeatmap = async () => {
    return this.restful.getSectorHeatmap();
  };

  private failScannerRequest(error: SafeError): SafePromise<ScannerProtos.IScannerResult> {
    return this.readScannerError(error).then(scannerError => {
      this.dispatch({
        error: scannerError,
        errorType: 'get_scanner_instruments_error',
        type: 'error',
      });
      return { err: scannerError };
    });
  }

  /**
   * Replaces the raw `Response` with the decoded error message, keeping the HTTP status on
   * `data.status` so callers can tell a rejected query (4xx) from an overloaded back end.
   */
  private async readScannerError(error: SafeError): Promise<SafeError<string, ScannerRequestErrorData>> {
    const errorBody = error.data;
    if (!(errorBody instanceof Response)) return error as SafeError<string, ScannerRequestErrorData>;

    const status = errorBody.status;
    const bytes = new Uint8Array(await errorBody.arrayBuffer());
    try {
      const decodedError = CommonProtos.Err.decode(bytes);
      return new SafeError(decodedError.message || 'Scanner request failed', error.type, {
        scannerError: decodedError,
        status,
      });
    } catch {
      const text = new TextDecoder().decode(bytes).slice(0, 300);
      return new SafeError(text || 'Scanner request failed', error.type, { status });
    }
  }

  private async decodeScannerResult(body: Response): SafePromise<ScannerProtos.IScannerResult> {
    try {
      const data = await body.arrayBuffer();
      const decodedData = ScannerProtos.ScannerResult.decode(new Uint8Array(data));

      this.dispatch({
        scannerResponse: decodedData,
        type: 'get_scanner_instruments',
      });

      return { ok: decodedData };
    } catch (error) {
      console.error('Protobuf decode error', error);
      return { err: new SafeError('Failed to decode scanner response', 'protobuf_decode_error', error) };
    }
  }
}
